import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DISPOSITION_OPTIONS = ['ANSWERED', 'BUSY', 'NO ANSWER', 'FAILED', 'CONGESTION'];
const ERROR_CODES = [
    { code: '403', title: 'Forbidden', desc: 'Sai IP hoặc chưa khai báo ACL' },
    { code: '404', title: 'Not Found', desc: 'Đầu số không tồn tại' },
    { code: '486', title: 'Busy', desc: 'Máy bận' },
    { code: '503', title: 'Service Unavailable', desc: 'Hết tài nguyên/Lỗi nhà mạng' }
];
const CARRIERS_LIST = ['MobiFone', 'VinaPhone', 'Viettel', 'GMobile', 'Vietnamobile', 'Cố định'];
const CARRIER_MAP = { GMobile: 'GMobile', Viettel: 'Viettel', MobiFone: 'MobiFone', VinaPhone: 'VinaPhone', Vietnamobile: 'Vietnamobile', CoDinh: 'Cố định' };

const s = {
    nav: { background: '#1e293b', color: 'white', padding: '12px 25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    card: { background: 'white', margin: '20px', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
    input: { padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' },
    filterInput: { padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', width: '90%', fontSize: '12px', marginTop: '5px', fontWeight: 'normal' },
    btn: { padding: '10px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: 'white' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
    th: { background: '#f8fafc', padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '14px' },
    td: { padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: '13px' },
    pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageBtn: { padding: '5px 12px', borderRadius: '4px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' },
    eIn: { border: '1px solid #d1d5db', padding: '5px 8px', width: '100px', textAlign: 'right', borderRadius: '4px', fontSize: '13px', background: '#fffef5', outline: 'none' },
    eInSm: { border: '1px solid #d1d5db', padding: '5px 6px', width: '65px', textAlign: 'center', borderRadius: '4px', fontSize: '13px', background: '#fffef5', outline: 'none' },
};

const fmt = (n) => {
    if (n === null || n === undefined || (n === 0)) return '-';
    return Math.round(n).toLocaleString('en-US');
};

function App() {
    const [view, setView] = useState('cdr');
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('user_info')));
    const [data, setData] = useState([]);
    const [analytics, setAnalytics] = useState([]);
    const [users, setUsers] = useState([]);
    const [emails, setEmails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(20);
    const [searchTerms, setSearchTerms] = useState({ direction: '', src: '', dst: '', disposition: '' });

    const todayVN = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date());
    const [filters, setFilters] = useState({ fromDate: todayVN, toDate: todayVN });
    const [login, setLogin] = useState({});
    const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
    const [newEmail, setNewEmail] = useState('');
    const [editingEmailId, setEditingEmailId] = useState(null);

    // === State đối soát ===
    const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
    const [exportYear, setExportYear] = useState(new Date().getFullYear());
    const [loadingReport, setLoadingReport] = useState(false);
    const [tableA, setTableA] = useState(() =>
        Object.fromEntries(CARRIERS_LIST.map(c => [c, { khoiTao_qty: 0, khoiTao_price: 0, duyTri_qty: 0, duyTri_price: 0 }]))
    );
    const [tableB, setTableB] = useState(() =>
        Object.fromEntries(CARRIERS_LIST.map(c => [c, { noiMang_min: 0, noiMang_price: 0, ngoaiMang_min: 0, ngoaiMang_price: 0 }]))
    );
    const [cuocGTGT, setCuocGTGT] = useState(0);
    const [phiDichVu, setPhiDichVu] = useState(0);
    const [dieuChinh, setDieuChinh] = useState(0);
    const [numDetails, setNumDetails] = useState([]);

    useEffect(() => {
        if (token) {
            if (view === 'cdr') fetchCDR();
            if (view === 'analytics') fetchAnalytics();
            if (view === 'admin') { fetchUsers(); fetchEmails(); }
        }
    }, [token, view]);

    useEffect(() => { setCurrentPage(1); }, [searchTerms, filters]);

    const fetchCDR = async () => {
        const res = await fetch(`/api/cdr?fromDate=${filters.fromDate}&toDate=${filters.toDate}`, { headers: { 'Authorization': token } });
        setData(await res.json());
    };

    const filteredData = data.filter(item =>
        (searchTerms.direction === '' || item.direction === searchTerms.direction) &&
        (item.src || '').toLowerCase().includes(searchTerms.src.toLowerCase()) &&
        (item.dst || '').toLowerCase().includes(searchTerms.dst.toLowerCase()) &&
        (item.disposition || '').toLowerCase().includes(searchTerms.disposition.toLowerCase())
    );
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const fetchAnalytics = async () => { const res = await fetch('/api/analytics/monthly', { headers: { 'Authorization': token } }); setAnalytics(await res.json()); };
    const fetchUsers = async () => { const res = await fetch('/api/users', { headers: { 'Authorization': token } }); setUsers(await res.json()); };
    const fetchEmails = async () => { const res = await fetch('/api/emails', { headers: { 'Authorization': token } }); setEmails(await res.json()); };

    // === Đối soát functions ===
    const fetchReconciliation = async () => {
        setLoadingReport(true);
        try {
            const res = await fetch(`/api/report/reconciliation?month=${exportMonth}&year=${exportYear}`, { headers: { 'Authorization': token } });
            const rawData = await res.json();
            const details = rawData.map(d => ({
                ...d, carrierDisplay: CARRIER_MAP[d.carrier] || d.carrier,
                khoiTao: 0, duyTri: 0, noiMang_price: 0, ngoaiMang_price: 0,
                noiMang_block: '6s+1', ngoaiMang_block: '6s+1'
            }));
            setNumDetails(details);

            const newB = {};
            CARRIERS_LIST.forEach(c => { newB[c] = { noiMang_min: 0, ngoaiMang_min: 0, noiMang_price: tableB[c]?.noiMang_price || 0, ngoaiMang_price: tableB[c]?.ngoaiMang_price || 0 }; });
            rawData.forEach(d => { const c = CARRIER_MAP[d.carrier]; if (c && newB[c]) { newB[c].noiMang_min += d.noi_mang_minutes || 0; newB[c].ngoaiMang_min += d.ngoai_mang_minutes || 0; } });
            CARRIERS_LIST.forEach(c => { newB[c].noiMang_min = Math.round(newB[c].noiMang_min * 100) / 100; newB[c].ngoaiMang_min = Math.round(newB[c].ngoaiMang_min * 100) / 100; });
            setTableB(newB);

            const newA = {};
            CARRIERS_LIST.forEach(c => { newA[c] = { khoiTao_qty: tableA[c]?.khoiTao_qty || 0, khoiTao_price: tableA[c]?.khoiTao_price || 0, duyTri_qty: 0, duyTri_price: tableA[c]?.duyTri_price || 0 }; });
            rawData.forEach(d => { const c = CARRIER_MAP[d.carrier]; if (c && newA[c]) newA[c].duyTri_qty++; });
            setTableA(newA);
        } catch (e) { console.error(e); }
        setLoadingReport(false);
    };

    const updateA = (carrier, field, val) => setTableA(p => ({ ...p, [carrier]: { ...p[carrier], [field]: parseFloat(val) || 0 } }));
    const updateB = (carrier, field, val) => setTableB(p => ({ ...p, [carrier]: { ...p[carrier], [field]: parseFloat(val) || 0 } }));
    const updateNum = (idx, field, val) => setNumDetails(p => { const u = [...p]; u[idx] = { ...u[idx], [field]: field.includes('block') ? val : (parseFloat(val) || 0) }; return u; });

    const calcA = (c, t) => t === 'khoiTao' ? tableA[c].khoiTao_qty * tableA[c].khoiTao_price : tableA[c].duyTri_qty * tableA[c].duyTri_price;
    const totalA = CARRIERS_LIST.reduce((sum, c) => sum + calcA(c, 'khoiTao') + calcA(c, 'duyTri'), 0);
    const calcBamt = (c, t) => t === 'noiMang' ? tableB[c].noiMang_min * tableB[c].noiMang_price : tableB[c].ngoaiMang_min * tableB[c].ngoaiMang_price;
    const totalBCalls = CARRIERS_LIST.reduce((sum, c) => sum + calcBamt(c, 'noiMang') + calcBamt(c, 'ngoaiMang'), 0);
    const totalB = totalBCalls + cuocGTGT + phiDichVu;
    const totalAB = totalA + totalB;
    const thueGTGT = Math.round(totalAB * 0.1);
    const tongCuoc = totalAB + thueGTGT;
    const tongThanhToan = tongCuoc + dieuChinh;

    const handleExportReconciliation = () => {
        const monthStr = String(exportMonth).padStart(2, '0');
        const wb = XLSX.utils.book_new();

        // Sheet 1: Tổng hợp
        const r1 = [
            ['BÁO CÁO ĐỐI SOÁT SẢN LƯỢNG - HƯỚNG GỌI RA'],
            [`Tháng: ${monthStr}/${exportYear}`, '', '', '', '', `Ngày xuất: ${todayVN}`],
            [],
            ['BẢNG A: PHÍ KHỞI TẠO, DUY TRÌ THUÊ BAO'],
            ['TT', 'Nhà mạng', 'Loại phí', 'Số lượng thuê bao/tháng', 'Đơn giá/thuê bao (VND)', 'Số tiền'],
        ];
        CARRIERS_LIST.forEach((c, i) => {
            r1.push([i + 1, c, c === 'Cố định' ? 'Phí khởi tạo' : 'Phí khởi tạo hotline', tableA[c].khoiTao_qty, tableA[c].khoiTao_price, calcA(c, 'khoiTao') || '-']);
            r1.push(['', '', 'Phí duy trì thuê bao', tableA[c].duyTri_qty, tableA[c].duyTri_price, calcA(c, 'duyTri') || '-']);
        });
        r1.push(['', '', '', '', 'Cộng cước dịch vụ (A)', totalA]);
        r1.push([]);
        r1.push(['BẢNG B: CƯỚC THOẠI THEO CÁC HƯỚNG']);
        r1.push(['TT', 'Cước thoại theo các hướng', '', 'Tổng số phút/Tháng', 'Đơn giá/phút (VND)', 'Số tiền']);
        CARRIERS_LIST.forEach((c, i) => {
            const t1 = c === 'Cố định' ? 'Nội hạt' : 'Nội mạng';
            const t2 = c === 'Cố định' ? 'Liên mạng / Liên tỉnh' : 'Ngoại mạng';
            r1.push([i + 1, c, t1, tableB[c].noiMang_min, tableB[c].noiMang_price, calcBamt(c, 'noiMang') || '-']);
            r1.push(['', '', t2, tableB[c].ngoaiMang_min, tableB[c].ngoaiMang_price, calcBamt(c, 'ngoaiMang') || '-']);
        });
        r1.push([7, 'Cước các dịch vụ GTGT', '', '', '', cuocGTGT || 0]);
        r1.push([8, 'Phí dịch vụ', '', '', '', phiDichVu || 0]);
        r1.push(['', '', '', '', 'Cộng cước dịch vụ (B)', totalB]);
        r1.push([]);
        r1.push(['Tổng phí và cước gọi', '', '', '', '', 'Số tiền']);
        r1.push(['Cước dịch vụ (A) + (B)', '', '', '', '', totalAB]);
        r1.push(['Thuế GTGT 10%', '', '', '', '', thueGTGT]);
        r1.push(['Tổng cước', '', '', '', '', tongCuoc]);
        r1.push(['Điều chỉnh tăng / giảm', '', '', '', '', dieuChinh]);
        r1.push(['Tổng số tiền cần thanh toán', '', '', '', '', tongThanhToan]);
        const ws1 = XLSX.utils.aoa_to_sheet(r1);
        ws1['!cols'] = [{ wch: 6 }, { wch: 18 }, { wch: 28 }, { wch: 24 }, { wch: 24 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws1, 'Tong_Hop');

        // Sheet 2: Chi tiết theo số
        const r2 = [
            ['CHI TIẾT THEO SỐ ĐIỆN THOẠI - HƯỚNG GỌI RA'],
            [`Tháng: ${monthStr}/${exportYear}`],
            [],
            ['Nhà mạng', 'Số điện thoại', 'Phí khởi tạo', 'Phí duy trì\n(gồm VAT)', 'Lưu lượng nội mạng\n(phút)', 'Block\nnội mạng', 'Giá nội mạng\n(gồm VAT)', 'Cước nội mạng\n(gồm VAT)', 'Lưu lượng ngoại mạng\n(phút)', 'Block\nngoại mạng', 'Giá ngoại mạng\n(gồm VAT)', 'Cước ngoại mạng\n(gồm VAT)', 'TỔNG'],
        ];
        numDetails.forEach(r => {
            const cNoi = Math.round(r.noi_mang_minutes * r.noiMang_price);
            const cNgoai = Math.round(r.ngoai_mang_minutes * r.ngoaiMang_price);
            r2.push([r.carrierDisplay, r.src, r.khoiTao, r.duyTri, r.noi_mang_minutes, r.noiMang_block, r.noiMang_price, cNoi, r.ngoai_mang_minutes, r.ngoaiMang_block, r.ngoaiMang_price, cNgoai, r.khoiTao + r.duyTri + cNoi + cNgoai]);
        });
        const tKT = numDetails.reduce((a, r) => a + r.khoiTao, 0);
        const tDT = numDetails.reduce((a, r) => a + r.duyTri, 0);
        const tNM = numDetails.reduce((a, r) => a + r.noi_mang_minutes, 0);
        const tNgM = numDetails.reduce((a, r) => a + r.ngoai_mang_minutes, 0);
        const tCN = numDetails.reduce((a, r) => a + Math.round(r.noi_mang_minutes * r.noiMang_price), 0);
        const tCNg = numDetails.reduce((a, r) => a + Math.round(r.ngoai_mang_minutes * r.ngoaiMang_price), 0);
        r2.push(['TỔNG CỘNG', '', tKT, tDT, Math.round(tNM * 100) / 100, '', '', tCN, Math.round(tNgM * 100) / 100, '', '', tCNg, tKT + tDT + tCN + tCNg]);
        const ws2 = XLSX.utils.aoa_to_sheet(r2);
        ws2['!cols'] = [{ wch: 14 }, { wch: 15 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 10 }, { wch: 16 }, { wch: 18 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Chi_Tiet_So');

        XLSX.writeFile(wb, `DoiSoat_T${monthStr}_${exportYear}.xlsx`);
    };

    const formatTimeVN = (dateInput) => {
        if (!dateInput) return "N/A";
        return new Intl.DateTimeFormat('vi-VN', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            timeZone: 'Asia/Ho_Chi_Minh', hour12: false
        }).format(new Date(dateInput));
    };

    const handleExport = (exportData, fileName) => {
        const formatted = exportData.map(item => ({
            "Thời gian": item.local_time_str || (item.start_time ? formatTimeVN(item.start_time) : item.date),
            "Hướng Cuộc Gọi": item.direction === 'INBOUND' ? 'GỌI VÀO' : (item.direction === 'OUTBOUND' ? 'GỌI RA' : 'NỘI BỘ'),
            "Số Gọi": item.src, "Số Nhận": item.dst,
            "Thời Gian Cuộc Gọi": `${item.billsec}s`, "Trạng thái": item.disposition
        }));
        const ws = XLSX.utils.json_to_sheet(formatted);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CDR_Report");
        XLSX.writeFile(wb, `${fileName}_${todayVN}.xlsx`);
    };

    // === Render helpers đối soát (Excel-like) ===
    const bdr = '1px solid #8c9bb5';
    const thX = { padding: '9px 10px', border: bdr, fontWeight: 'bold', fontSize: '13px', color: 'white', textAlign: 'center', whiteSpace: 'nowrap' };
    const tdX = { padding: '7px 10px', border: bdr, fontSize: '13px', verticalAlign: 'middle' };
    const tdXC = { ...tdX, textAlign: 'center' };
    const tdXR = { ...tdX, textAlign: 'right', fontWeight: 'bold' };
    const tblX = { width: '100%', borderCollapse: 'collapse', marginTop: 0, border: bdr };

    // Totals cho bảng 2
    const numTotals = numDetails.reduce((acc, r) => {
        const cN = Math.round(r.noi_mang_minutes * r.noiMang_price);
        const cNg = Math.round(r.ngoai_mang_minutes * r.ngoaiMang_price);
        return {
            khoiTao: acc.khoiTao + r.khoiTao, duyTri: acc.duyTri + r.duyTri,
            noiMin: acc.noiMin + r.noi_mang_minutes, ngoaiMin: acc.ngoaiMin + r.ngoai_mang_minutes,
            cuocNoi: acc.cuocNoi + cN, cuocNgoai: acc.cuocNgoai + cNg, total: acc.total + r.khoiTao + r.duyTri + cN + cNg
        };
    }, { khoiTao: 0, duyTri: 0, noiMin: 0, ngoaiMin: 0, cuocNoi: 0, cuocNgoai: 0, total: 0 });

    if (!token) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a' }}>
            <div style={{ ...s.card, width: '320px', textAlign: 'center' }}>
                <h2 style={{color: '#3b82f6'}}>ZIPAI CDR</h2>
                <input placeholder="Username" style={{ ...s.input, width: '90%', marginBottom: '10px' }} onChange={e => setLogin({ ...login, username: e.target.value })} />
                <input type="password" placeholder="Password" style={{ ...s.input, width: '90%', marginBottom: '20px' }} onChange={e => setLogin({ ...login, password: e.target.value })} />
                <button style={{ ...s.btn, background: '#3b82f6', width: '100%' }} onClick={async () => {
                    const res = await fetch('/api/login', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(login)});
                    const d = await res.json();
                    if(d.token) { localStorage.setItem('token', d.token); localStorage.setItem('user_info', JSON.stringify(d)); setToken(d.token); setUser(d); }
                    else alert("Sai tài khoản!");
                }}>ĐĂNG NHẬP</button>
            </div>
        </div>
    );

    return (
        <div style={{ background: '#f1f5f9', minHeight: '100vh', fontFamily: 'sans-serif' }}>
            <nav style={s.nav}>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <span style={{ fontWeight: '800', fontSize: '18px' }}>3GTEL CDR</span>
                    {['cdr', 'analytics', 'reconciliation', 'help'].map(v => (
                        <button key={v} onClick={() => setView(v)} style={{ background: 'none', border: 'none', color: view === v ? '#3b82f6' : 'white', cursor: 'pointer', fontWeight: 'bold' }}>
                            {{ cdr: 'DỮ LIỆU CƯỚC', analytics: 'BÁO CÁO', reconciliation: 'ĐỐI SOÁT', help: 'MÃ LỖI' }[v]}
                        </button>
                    ))}
                    {user.role === 'admin' && <button onClick={() => setView('admin')} style={{ background: 'none', border: 'none', color: view === 'admin' ? '#3b82f6' : 'white', cursor: 'pointer', fontWeight: 'bold' }}>QUẢN TRỊ</button>}
                </div>
                <button onClick={() => { localStorage.clear(); setToken(null); }} style={{ ...s.btn, background: '#ef4444', padding: '5px 15px' }}>THOÁT</button>
            </nav>

            {/* ===== CDR VIEW ===== */}
            {view === 'cdr' && (
                <div style={s.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <div>
                            <input type="date" style={s.input} value={filters.fromDate} onChange={e => setFilters({...filters, fromDate:e.target.value})} />
                            <input type="date" style={{...s.input, marginLeft:'5px'}} value={filters.toDate} onChange={e => setFilters({...filters, toDate:e.target.value})} />
                            <button style={{ ...s.btn, background: '#3b82f6', marginLeft: '10px' }} onClick={fetchCDR}>TRUY VẤN</button>
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap: '15px'}}>
                            <span style={{fontSize: '13px', color: '#64748b'}}>Dòng {indexOfFirstRow + 1} - {Math.min(indexOfLastRow, filteredData.length)} / <b>{filteredData.length}</b></span>
                            <button style={{ ...s.btn, background: '#10b981' }} onClick={() => handleExport(filteredData, 'Full_CDR_Report')}>XUẤT EXCEL</button>
                        </div>
                    </div>
                    <table style={s.table}>
                        <thead><tr>
                            <th style={s.th}>Thời Gian (GMT+7)</th>
                            <th style={s.th}>Hướng<select style={s.filterInput} value={searchTerms.direction} onChange={e => setSearchTerms({...searchTerms, direction: e.target.value})}><option value="">Tất cả</option><option value="INBOUND">GỌI VÀO</option><option value="OUTBOUND">GỌI RA</option></select></th>
                            <th style={s.th}>Số Gọi<input placeholder="Lọc..." style={s.filterInput} value={searchTerms.src} onChange={e => setSearchTerms({...searchTerms, src: e.target.value})} /></th>
                            <th style={s.th}>Số Nhận<input placeholder="Lọc..." style={s.filterInput} value={searchTerms.dst} onChange={e => setSearchTerms({...searchTerms, dst: e.target.value})} /></th>
                            <th style={s.th}>Thời Gian</th>
                            <th style={s.th}>Trạng thái<select style={s.filterInput} value={searchTerms.disposition} onChange={e => setSearchTerms({...searchTerms, disposition: e.target.value})}><option value="">Tất cả</option>{DISPOSITION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select></th>
                        </tr></thead>
                        <tbody>{currentRows.map(i => (
                            <tr key={i.id}>
                                <td style={s.td}>{i.local_time_str || formatTimeVN(i.start_time)}</td>
                                <td style={s.td}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', color: 'white', background: i.direction === 'INBOUND' ? '#3b82f6' : (i.direction === 'OUTBOUND' ? '#f59e0b' : '#64748b') }}>{i.direction === 'INBOUND' ? 'VÀO' : (i.direction === 'OUTBOUND' ? 'RA' : 'NỘI BỘ')}</span></td>
                                <td style={s.td}>{i.src}</td><td style={s.td}>{i.dst}</td><td style={s.td}>{i.billsec}s</td>
                                <td style={{...s.td, color: i.disposition === 'ANSWERED' ? '#10b981' : '#ef4444', fontWeight:'bold'}}>{i.disposition}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={s.pagination}>
                            <button style={{...s.pageBtn, opacity: currentPage === 1 ? 0.5 : 1}} disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Trước</button>
                            <span style={{fontSize: '14px', fontWeight: 'bold'}}>Trang {currentPage} / {totalPages}</span>
                            <button style={{...s.pageBtn, opacity: currentPage === totalPages ? 0.5 : 1}} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Sau</button>
                        </div>
                    )}
                </div>
            )}

            {/* ===== ANALYTICS VIEW ===== */}
            {view === 'analytics' && (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ color: '#1e293b', marginLeft: '20px' }}>Thống kê Sản lượng Tháng</h3>
                    {['INBOUND', 'OUTBOUND'].map(dir => {
                        const fa = analytics.filter(item => item.direction === dir);
                        if (fa.length === 0) return null;
                        return (
                            <div key={dir} style={{ marginBottom: '40px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '20px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: dir === 'INBOUND' ? '#3b82f6' : '#f59e0b' }}></div>
                                    <h4 style={{ margin: 0 }}>HƯỚNG {dir === 'INBOUND' ? 'GỌI VÀO' : 'GỌI RA'}</h4>
                                </div>
                                <div style={{ ...s.card, height: '300px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={fa}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" /><YAxis /><Tooltip /><Legend />
                                            <Bar dataKey="total_minutes" name="Phút" fill={dir === 'INBOUND' ? '#3b82f6' : '#f59e0b'} />
                                            <Bar dataKey="total_calls" name="Cuộc gọi" fill="#94a3b8" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div style={s.card}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <span>Chi tiết dữ liệu ngày</span>
                                        <button style={{ ...s.btn, background: '#10b981', padding: '5px 10px', fontSize: '12px' }} onClick={() => handleExport(fa, `Bao_Cao_${dir}`)}>XUẤT EXCEL {dir}</button>
                                    </div>
                                    <table style={s.table}>
                                        <thead><tr><th style={s.th}>NGÀY</th><th style={s.th}>TỔNG GỌI</th><th style={s.th}>THÀNH CÔNG</th><th style={s.th}>ASR (%)</th><th style={s.th}>PHÚT</th></tr></thead>
                                        <tbody>{fa.map(r => (<tr key={r.date + r.direction}><td style={s.td}>{r.date}</td><td style={s.td}>{r.total_calls}</td><td style={s.td}>{r.answered_calls}</td><td style={s.td}>{r.success_rate}%</td><td style={s.td}>{r.total_minutes}</td></tr>))}</tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ===== ĐỐI SOÁT VIEW ===== */}
            {view === 'reconciliation' && (
                <div style={{ padding: '20px' }}>
                    <div style={s.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ color: '#1e293b', margin: 0 }}>Đối soát Sản lượng - Hướng Gọi Ra</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <select style={s.input} value={exportMonth} onChange={e => setExportMonth(parseInt(e.target.value))}>
                                    {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>Tháng {i+1}</option>)}
                                </select>
                                <select style={s.input} value={exportYear} onChange={e => setExportYear(parseInt(e.target.value))}>
                                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                                <button style={{ ...s.btn, background: '#3b82f6' }} onClick={fetchReconciliation}>
                                    {loadingReport ? 'Đang tải...' : 'TẢI DỮ LIỆU CDR'}
                                </button>
                                <button style={{ ...s.btn, background: '#10b981' }} onClick={handleExportReconciliation}>XUẤT EXCEL</button>
                            </div>
                        </div>

                        {/* ===== BẢNG A ===== */}
                        <table style={tblX}>
                            <thead>
                                <tr style={{ background: '#217346' }}>
                                    <th style={{ ...thX, width: '40px' }}>TT</th>
                                    <th style={{ ...thX, width: '130px', textAlign: 'left' }}>Nhà mạng</th>
                                    <th style={{ ...thX, textAlign: 'left' }}>Phí khởi tạo, duy trì thuê bao</th>
                                    <th style={thX}>Số lượng thuê bao/tháng</th>
                                    <th style={thX}>Đơn giá/thuê bao (VND)</th>
                                    <th style={{ ...thX, width: '150px' }}>Số tiền</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CARRIERS_LIST.map((c, i) => (
                                    <React.Fragment key={c}>
                                        <tr style={{ background: i % 2 === 0 ? '#fff' : '#f2f7f5' }}>
                                            <td style={tdXC} rowSpan={2}><b>{i + 1}</b></td>
                                            <td style={{ ...tdX, fontWeight: 'bold' }} rowSpan={2}>{c}</td>
                                            <td style={tdX}>{c === 'Cố định' ? 'Phí khởi tạo' : 'Phí khởi tạo hotline'}</td>
                                            <td style={tdXC}><input type="number" style={s.eIn} value={tableA[c].khoiTao_qty || ''} onChange={e => updateA(c, 'khoiTao_qty', e.target.value)} /></td>
                                            <td style={tdXC}><input type="number" style={s.eIn} value={tableA[c].khoiTao_price || ''} onChange={e => updateA(c, 'khoiTao_price', e.target.value)} /></td>
                                            <td style={tdXR}>{fmt(calcA(c, 'khoiTao'))}</td>
                                        </tr>
                                        <tr style={{ background: i % 2 === 0 ? '#fff' : '#f2f7f5' }}>
                                            <td style={tdX}>Phí duy trì thuê bao</td>
                                            <td style={tdXC}><input type="number" style={s.eIn} value={tableA[c].duyTri_qty || ''} onChange={e => updateA(c, 'duyTri_qty', e.target.value)} /></td>
                                            <td style={tdXC}><input type="number" style={s.eIn} value={tableA[c].duyTri_price || ''} onChange={e => updateA(c, 'duyTri_price', e.target.value)} /></td>
                                            <td style={tdXR}>{fmt(calcA(c, 'duyTri'))}</td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                                <tr style={{ background: '#dff0d8' }}>
                                    <td colSpan={5} style={{ ...tdX, textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>Cộng cước dịch vụ (A)</td>
                                    <td style={{ ...tdXR, fontSize: '14px', color: '#217346' }}>{fmt(totalA)}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* ===== BẢNG B ===== */}
                        <table style={{ ...tblX, marginTop: '25px' }}>
                            <thead>
                                <tr style={{ background: '#217346' }}>
                                    <th style={{ ...thX, width: '40px' }}>TT</th>
                                    <th style={{ ...thX, width: '130px', textAlign: 'left' }} colSpan={2}>Cước thoại theo các hướng</th>
                                    <th style={thX}>Tổng số phút/Tháng</th>
                                    <th style={thX}>Đơn giá/phút (VND)</th>
                                    <th style={{ ...thX, width: '150px' }}>Số tiền</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CARRIERS_LIST.map((c, i) => {
                                    const t1 = c === 'Cố định' ? 'Nội hạt' : 'Nội mạng';
                                    const t2 = c === 'Cố định' ? 'Liên mạng / Liên tỉnh' : 'Ngoại mạng';
                                    return (
                                        <React.Fragment key={c}>
                                            <tr style={{ background: i % 2 === 0 ? '#fff' : '#f2f7f5' }}>
                                                <td style={tdXC} rowSpan={2}><b>{i + 1}</b></td>
                                                <td style={{ ...tdX, fontWeight: 'bold' }} rowSpan={2}>{c}</td>
                                                <td style={tdX}>{t1}</td>
                                                <td style={tdXC}><input type="number" style={s.eIn} value={tableB[c].noiMang_min || ''} onChange={e => updateB(c, 'noiMang_min', e.target.value)} /></td>
                                                <td style={tdXC}><input type="number" style={s.eIn} value={tableB[c].noiMang_price || ''} onChange={e => updateB(c, 'noiMang_price', e.target.value)} /></td>
                                                <td style={tdXR}>{fmt(calcBamt(c, 'noiMang'))}</td>
                                            </tr>
                                            <tr style={{ background: i % 2 === 0 ? '#fff' : '#f2f7f5' }}>
                                                <td style={tdX}>{t2}</td>
                                                <td style={tdXC}><input type="number" style={s.eIn} value={tableB[c].ngoaiMang_min || ''} onChange={e => updateB(c, 'ngoaiMang_min', e.target.value)} /></td>
                                                <td style={tdXC}><input type="number" style={s.eIn} value={tableB[c].ngoaiMang_price || ''} onChange={e => updateB(c, 'ngoaiMang_price', e.target.value)} /></td>
                                                <td style={tdXR}>{fmt(calcBamt(c, 'ngoaiMang'))}</td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}
                                <tr style={{ background: '#fff' }}>
                                    <td style={tdXC}><b>7</b></td>
                                    <td style={tdX} colSpan={2}><b>Cước các dịch vụ GTGT</b></td>
                                    <td style={tdX}></td>
                                    <td style={tdX}></td>
                                    <td style={tdXR}><input type="number" style={s.eIn} value={cuocGTGT || ''} onChange={e => setCuocGTGT(parseFloat(e.target.value) || 0)} /></td>
                                </tr>
                                <tr style={{ background: '#f2f7f5' }}>
                                    <td style={tdXC}><b>8</b></td>
                                    <td style={tdX} colSpan={2}><b>Phí dịch vụ</b></td>
                                    <td style={tdX}></td>
                                    <td style={tdX}></td>
                                    <td style={tdXR}><input type="number" style={s.eIn} value={phiDichVu || ''} onChange={e => setPhiDichVu(parseFloat(e.target.value) || 0)} /></td>
                                </tr>
                                <tr style={{ background: '#dff0d8' }}>
                                    <td colSpan={5} style={{ ...tdX, textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>Cộng cước dịch vụ (B)</td>
                                    <td style={{ ...tdXR, fontSize: '14px', color: '#217346' }}>{fmt(totalB)}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* ===== TỔNG HỢP ===== */}
                        <table style={{ ...tblX, marginTop: '25px' }}>
                            <thead>
                                <tr style={{ background: '#217346' }}>
                                    <th style={{ ...thX, textAlign: 'left' }} colSpan={2}>Tổng phí và cước gọi</th>
                                    <th style={{ ...thX, width: '180px' }}>Số tiền</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td colSpan={2} style={tdX}>Cước dịch vụ (A) + (B)</td><td style={tdXR}>{fmt(totalAB)}</td></tr>
                                <tr style={{ background: '#f2f7f5' }}><td colSpan={2} style={tdX}>Thuế GTGT 10%</td><td style={tdXR}>{fmt(thueGTGT)}</td></tr>
                                <tr style={{ background: '#dff0d8' }}><td colSpan={2} style={{ ...tdX, fontWeight: 'bold' }}>Tổng cước</td><td style={tdXR}>{fmt(tongCuoc)}</td></tr>
                                <tr>
                                    <td colSpan={2} style={tdX}>Điều chỉnh tăng / giảm</td>
                                    <td style={tdXR}><input type="number" style={s.eIn} value={dieuChinh || ''} onChange={e => setDieuChinh(parseFloat(e.target.value) || 0)} /></td>
                                </tr>
                                <tr style={{ background: '#217346' }}>
                                    <td colSpan={2} style={{ ...tdX, fontWeight: 'bold', fontSize: '15px', color: 'white' }}>Tổng số tiền cần thanh toán</td>
                                    <td style={{ ...tdX, textAlign: 'right', fontWeight: 'bold', fontSize: '16px', color: '#fff' }}>{fmt(tongThanhToan)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* ===== BẢNG 2: CHI TIẾT THEO SỐ ===== */}
                    <div style={s.card}>
                        <h4 style={{ color: '#1e293b', marginTop: 0 }}>Chi tiết theo Số điện thoại</h4>
                        {numDetails.length === 0 ? (
                            <p style={{ color: '#94a3b8', textAlign: 'center', padding: '30px' }}>Bấm "TẢI DỮ LIỆU CDR" để lấy dữ liệu cuộc gọi ra theo từng số</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ ...tblX, minWidth: '1400px' }}>
                                    <thead>
                                        <tr style={{ background: '#1a4a6e' }}>
                                            <th style={thX} rowSpan={2}>Nhà mạng</th>
                                            <th style={thX} rowSpan={2}>Số ĐT</th>
                                            <th style={thX} rowSpan={2}>Phí khởi tạo</th>
                                            <th style={thX} rowSpan={2}>Phí duy trì<br/>(gồm VAT)</th>
                                            <th style={{ ...thX, background: '#2e7d32' }} colSpan={4}>NỘI MẠNG</th>
                                            <th style={{ ...thX, background: '#e65100' }} colSpan={4}>NGOẠI MẠNG</th>
                                            <th style={{ ...thX, background: '#4a148c' }} rowSpan={2}>TỔNG</th>
                                        </tr>
                                        <tr style={{ background: '#1a4a6e' }}>
                                            <th style={{ ...thX, background: '#388e3c', fontSize: '11px' }}>Lưu lượng<br/>(phút)</th>
                                            <th style={{ ...thX, background: '#388e3c', fontSize: '11px' }}>Block</th>
                                            <th style={{ ...thX, background: '#388e3c', fontSize: '11px' }}>Giá<br/>(gồm VAT)</th>
                                            <th style={{ ...thX, background: '#388e3c', fontSize: '11px' }}>Cước<br/>(gồm VAT)</th>
                                            <th style={{ ...thX, background: '#ef6c00', fontSize: '11px' }}>Lưu lượng<br/>(phút)</th>
                                            <th style={{ ...thX, background: '#ef6c00', fontSize: '11px' }}>Block</th>
                                            <th style={{ ...thX, background: '#ef6c00', fontSize: '11px' }}>Giá<br/>(gồm VAT)</th>
                                            <th style={{ ...thX, background: '#ef6c00', fontSize: '11px' }}>Cước<br/>(gồm VAT)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {numDetails.map((r, idx) => {
                                            const cNoi = Math.round(r.noi_mang_minutes * r.noiMang_price);
                                            const cNgoai = Math.round(r.ngoai_mang_minutes * r.ngoaiMang_price);
                                            const total = r.khoiTao + r.duyTri + cNoi + cNgoai;
                                            return (
                                                <tr key={r.src} style={{ background: idx % 2 === 0 ? '#fff' : '#f5f7fa' }}>
                                                    <td style={{ ...tdX, fontWeight: 'bold' }}>{r.carrierDisplay}</td>
                                                    <td style={{ ...tdX, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.src}</td>
                                                    <td style={tdXC}><input type="number" style={s.eIn} value={r.khoiTao || ''} onChange={e => updateNum(idx, 'khoiTao', e.target.value)} /></td>
                                                    <td style={tdXC}><input type="number" style={s.eIn} value={r.duyTri || ''} onChange={e => updateNum(idx, 'duyTri', e.target.value)} /></td>
                                                    <td style={{ ...tdXC, background: idx % 2 === 0 ? '#f1f8e9' : '#e8f5e9' }}><input type="number" style={s.eIn} value={r.noi_mang_minutes || ''} onChange={e => updateNum(idx, 'noi_mang_minutes', e.target.value)} /></td>
                                                    <td style={{ ...tdXC, background: idx % 2 === 0 ? '#f1f8e9' : '#e8f5e9' }}><input style={s.eInSm} value={r.noiMang_block} onChange={e => updateNum(idx, 'noiMang_block', e.target.value)} /></td>
                                                    <td style={{ ...tdXC, background: idx % 2 === 0 ? '#f1f8e9' : '#e8f5e9' }}><input type="number" style={s.eIn} value={r.noiMang_price || ''} onChange={e => updateNum(idx, 'noiMang_price', e.target.value)} /></td>
                                                    <td style={{ ...tdXR, background: idx % 2 === 0 ? '#f1f8e9' : '#e8f5e9' }}>{fmt(cNoi)}</td>
                                                    <td style={{ ...tdXC, background: idx % 2 === 0 ? '#fff3e0' : '#ffe0b2' }}><input type="number" style={s.eIn} value={r.ngoai_mang_minutes || ''} onChange={e => updateNum(idx, 'ngoai_mang_minutes', e.target.value)} /></td>
                                                    <td style={{ ...tdXC, background: idx % 2 === 0 ? '#fff3e0' : '#ffe0b2' }}><input style={s.eInSm} value={r.ngoaiMang_block} onChange={e => updateNum(idx, 'ngoaiMang_block', e.target.value)} /></td>
                                                    <td style={{ ...tdXC, background: idx % 2 === 0 ? '#fff3e0' : '#ffe0b2' }}><input type="number" style={s.eIn} value={r.ngoaiMang_price || ''} onChange={e => updateNum(idx, 'ngoaiMang_price', e.target.value)} /></td>
                                                    <td style={{ ...tdXR, background: idx % 2 === 0 ? '#fff3e0' : '#ffe0b2' }}>{fmt(cNgoai)}</td>
                                                    <td style={{ ...tdXR, background: '#f3e5f5', color: '#6a1b9a', fontSize: '13px' }}>{fmt(total)}</td>
                                                </tr>
                                            );
                                        })}
                                        <tr style={{ background: '#e0e0e0' }}>
                                            <td colSpan={2} style={{ ...tdX, fontWeight: 'bold', fontSize: '13px' }}>TỔNG CỘNG</td>
                                            <td style={tdXR}>{fmt(numTotals.khoiTao)}</td>
                                            <td style={tdXR}>{fmt(numTotals.duyTri)}</td>
                                            <td style={{ ...tdXR, background: '#c8e6c9' }}>{Math.round(numTotals.noiMin * 100) / 100}</td>
                                            <td style={{ ...tdX, background: '#c8e6c9' }}></td>
                                            <td style={{ ...tdX, background: '#c8e6c9' }}></td>
                                            <td style={{ ...tdXR, background: '#c8e6c9' }}>{fmt(numTotals.cuocNoi)}</td>
                                            <td style={{ ...tdXR, background: '#ffe0b2' }}>{Math.round(numTotals.ngoaiMin * 100) / 100}</td>
                                            <td style={{ ...tdX, background: '#ffe0b2' }}></td>
                                            <td style={{ ...tdX, background: '#ffe0b2' }}></td>
                                            <td style={{ ...tdXR, background: '#ffe0b2' }}>{fmt(numTotals.cuocNgoai)}</td>
                                            <td style={{ ...tdXR, background: '#ce93d8', color: '#4a148c', fontSize: '14px' }}>{fmt(numTotals.total)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===== HELP VIEW ===== */}
            {view === 'help' && (
                <div style={s.card}>
                    <h3>Tra cứu mã lỗi Asterisk / SIP</h3>
                    <table style={s.table}>
                        <thead><tr><th style={s.th}>MÃ</th><th style={s.th}>TÊN</th><th style={s.th}>MÔ TẢ CHI TIẾT</th></tr></thead>
                        <tbody>{ERROR_CODES.map(e => (<tr key={e.code}><td style={s.td}><b>{e.code}</b></td><td style={s.td}>{e.title}</td><td style={s.td}>{e.desc}</td></tr>))}</tbody>
                    </table>
                </div>
            )}

            {/* ===== ADMIN VIEW ===== */}
            {view === 'admin' && (
                <div style={{ padding: '20px' }}>
                    <div style={s.card}>
                        <h4>Quản lý Tài khoản</h4>
                        <div style={{display:'flex', gap:'10px', marginBottom:'15px'}}>
                            <input style={s.input} placeholder="Username" onChange={e => setNewUser({...newUser, username:e.target.value})} />
                            <input style={s.input} placeholder="Password" type="password" onChange={e => setNewUser({...newUser, password:e.target.value})} />
                            <select style={s.input} onChange={e => setNewUser({...newUser, role:e.target.value})}><option value="user">User</option><option value="admin">Admin</option></select>
                            <button style={{...s.btn, background:'#10b981'}} onClick={async () => { await fetch('/api/users',{method:'POST',headers:{'Content-Type':'application/json','Authorization':token},body:JSON.stringify(newUser)}); fetchUsers(); }}>THÊM USER</button>
                        </div>
                        <table style={s.table}>
                            <thead><tr><th style={s.th}>Username</th><th style={s.th}>Role</th><th style={s.th}>Thao tác</th></tr></thead>
                            <tbody>{users.map(u => (<tr key={u.id}><td style={s.td}>{u.username}</td><td style={s.td}>{u.role}</td><td style={s.td}><button style={{color:'red', border:'none', background:'none', cursor:'pointer'}} onClick={async()=>{if(window.confirm("Xác nhận xóa?")){await fetch('/api/users/'+u.id,{method:'DELETE',headers:{'Authorization':token}}); fetchUsers();}}}>Xóa</button></td></tr>))}</tbody>
                        </table>
                    </div>
                    <div style={s.card}>
                        <h4>Quản lý Email nhận báo cáo (GMT+7)</h4>
                        <div style={{display:'flex', gap:'10px', marginBottom:'15px'}}>
                            <input style={{...s.input, flex:1}} value={newEmail} placeholder="Nhập email..." onChange={e => setNewEmail(e.target.value)} />
                            <button style={{...s.btn, background: editingEmailId ? '#f59e0b' : '#10b981'}} onClick={async () => {
                                const method = editingEmailId ? 'PUT' : 'POST';
                                const url = editingEmailId ? `/api/emails/${editingEmailId}` : '/api/emails';
                                await fetch(url, {method, headers:{'Content-Type':'application/json','Authorization':token}, body:JSON.stringify({email:newEmail})});
                                setNewEmail(''); setEditingEmailId(null); fetchEmails();
                            }}>{editingEmailId ? 'CẬP NHẬT' : 'THÊM EMAIL'}</button>
                        </div>
                        <table style={s.table}>
                            <thead><tr><th style={s.th}>Email</th><th style={s.th}>Thao tác</th></tr></thead>
                            <tbody>{emails.map(e => (
                                <tr key={e.id}>
                                    <td style={s.td}>{e.email}</td>
                                    <td style={s.td}>
                                        <button style={{color:'#3b82f6', border:'none', background:'none', cursor:'pointer', marginRight:'10px'}} onClick={()=>{setEditingEmailId(e.id); setNewEmail(e.email);}}>Sửa</button>
                                        <button style={{color:'red', border:'none', background:'none', cursor:'pointer'}} onClick={async()=>{if(window.confirm("Xóa email này?")){await fetch('/api/emails/'+e.id,{method:'DELETE',headers:{'Authorization':token}}); fetchEmails();}}}>Xóa</button>
                                    </td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;